import {createSelector} from '@reduxjs/toolkit'

import {groupBy, includes} from '@shared/helpers/collection'
import {eq} from '@shared/helpers/lang'
import {sum, sumBy} from '@shared/helpers/math'
import {mapValues, mergeWith, omit, pick, deepMergeWithValid} from '@shared/helpers/object'

import {ProductOrderStatus} from '@web/core/constants'

import {
    LogeyeRewardPoint,
    LogeyeRewardPointByTitle,
    PurchaseRewardAdmin,
    PurchaseRewardPoint,
    ReviewRewardPoint,
    RewardItem,
    ReviewSubType,
    RewardType,
    RootState,
    PurchaseSubType,
    ReviewSubTypeType,
} from './interfaces'
import {isReviewSubType, createInitialPurchaseRewardPoint, createInitialReviewRewardPoint} from './utils'

export const rewardSelector = (state: RootState) => state.reward

export const benefitCardRewardTidSelector = (state: RootState) => state.reward.benefitCardRewardTid

export const rewardApiStatusSelector = createSelector(rewardSelector, ({apiStatus}) => {
    return {
        purchase: apiStatus.PURCHASE,
        review: apiStatus.REVIEW,
        benefitCard: apiStatus.BENEFIT_CARD,
        expectedMembership: apiStatus.EXPECTED_MEMBERSHIP,
        event: apiStatus.EVENT,
    }
})

export const rewardTargetProductInfoSelector = createSelector(
    (state: RootState) => state.reward.products,
    (products) => {
        const expectedMembershipProducts = products.filter(
            (product) => product.productOrderStatusType === ProductOrderStatus.PAYMENT_WAITING && product.naverBenefit,
        )

        const productsInfo = expectedMembershipProducts.map((product) => {
            return {
                lCatId: product.largeCategoryId ?? null,
                mCatId: product.middleCategoryId ?? null,
                sCatId: product.smallCategoryId ?? null,
                svcType: product.serviceType ?? null,
                merchantNo: product.merchantNo ?? null,
                productPayAmount: [
                    {
                        productAmount: product.payAmountInfo?.productAmount ?? 0,
                        totalPayAmount: product.payAmountInfo?.totalPayAmount ?? 0,
                        totalPointPayAmount: product.payAmountInfo?.totalPointPayAmount ?? 0,
                        scheduledChannelTypeCode: product.payAmountInfo?.scheduledChannelTypeCode ?? null,
                        rentalOrderClassCode: product.payAmountInfo?.rentalOrderClassCode ?? null,
                        initOrderTotalPromotionApplyAmount:
                            product.payAmountInfo?.initOrderTotalPromotionApplyAmount ?? null,
                    },
                ],
            }
        })

        return {
            allProductOrderNos: products.map((product) => product.itemId ?? ''),
            expectedMembershipProducts: {
                productOrderNos: expectedMembershipProducts.map((product) => product.itemId ?? ''),
                productsInfo,
            },
        }
    },
)

export const rewardApiPurchaseSelector = createSelector(rewardApiStatusSelector, ({purchase}) => {
    return {
        isResolved: purchase === 'RESOLVED',
        isRejected: purchase === 'REJECTED',
    }
})

const calcPurchaseRewardByItemIdTotal = (purchaseRewardPoint: PurchaseRewardPoint) => {
    /**
     * number point의 총합
     * object인 sellerCustomerManagementByTitle, logeyeRewardPointByTitle 제외
     * 최대 예상적립포인트인 expectedNaverBankbook, expectedChargePoint 제외
     * total 제외
     */
    const intPointSum = sum(
        Object.values(
            omit(
                [
                    'sellerCustomerManagementByTitle',
                    'logeyeRewardPointByTitle',
                    'expectedNaverBankbook',
                    'expectedChargePoint',
                    'totalRewardPoint',
                ],
                purchaseRewardPoint,
            ),
        ),
    )
    const sellerCustomerManagementMileageSum = Object.values(
        purchaseRewardPoint.sellerCustomerManagementByTitle ?? {},
    ).reduce((prev, next) => {
        return prev + next
    }, 0)
    const logeyeRewardPointSum = Object.values(purchaseRewardPoint.logeyeRewardPointByTitle ?? {}).reduce(
        (prev, {rewardAmount}) => {
            return prev + rewardAmount
        },
        0,
    )
    return intPointSum + sellerCustomerManagementMileageSum + logeyeRewardPointSum
}

export const purchaseRewardSelector = createSelector(rewardSelector, (reward) => {
    const {purchaseRewardPoint, expectedChargePoint, expectedNaverBankbook} = reward

    const result = deepMergeWithValid(
        {
            total: {
                ...purchaseRewardPoint.total,
                adminAdditional: {
                    ...purchaseRewardPoint.total.adminAdditional,
                    title: eq(purchaseRewardPoint.total.adminAdditional.adminType, PurchaseRewardAdmin.RENTAL)
                        ? '렌탈료 정기결제 적립'
                        : eq(purchaseRewardPoint.total.adminAdditional.adminType, PurchaseRewardAdmin.SUBSCRIPTION)
                          ? '정기구독 추가적립'
                          : '상품구매 추가적립',
                },
            },
        },
        createInitialPurchaseRewardPoint(),
    )
    const pointsByOrderSheetItemId = purchaseRewardPoint.pointsByOrderSheetItemId
    Object.entries(pointsByOrderSheetItemId).forEach(([itemId, pointByOrderSheetItemId]) => {
        /** pointsByOrderSheetItemId의 total 재계산하여 할당 */
        result.pointsByOrderSheetItemId[itemId] = {
            ...pointByOrderSheetItemId,
            totalRewardPoint: calcPurchaseRewardByItemIdTotal(pointByOrderSheetItemId),
        }
        /** purchaseRewardPoint의 적립중 'number'인 itemId별 포인트의 속성을 덧셈 */
        Object.keys(pick(['expectedNaverBankbook', 'expectedChargePoint', 'totalRewardPoint'], result.total)).forEach(
            (originKey) => {
                const key = originKey as keyof Pick<
                    PurchaseRewardPoint,
                    'expectedNaverBankbook' | 'expectedChargePoint' | 'totalRewardPoint'
                >
                result.total[key] += result.pointsByOrderSheetItemId[itemId]?.[key] ?? 0
            },
        )

        /** purchaseRewardPoint의 적립중 '객체'인 itemId별 포인트의 속성을 덧셈 */
        Object.keys(
            omit(
                [
                    'expectedNaverBankbook',
                    'expectedChargePoint',
                    'totalRewardPoint',
                    'sellerCustomerManagementByTitle',
                    'logeyeRewardPointByTitle',
                ],
                result.total,
            ),
        ).forEach((originKey) => {
            const key = originKey as keyof Omit<
                PurchaseRewardPoint,
                | 'expectedNaverBankbook'
                | 'expectedChargePoint'
                | 'totalRewardPoint'
                | 'sellerCustomerManagementByTitle'
                | 'logeyeRewardPointByTitle'
            >

            result.total[key] = {
                title: result.total[key]?.title ?? '',
                amount: (pointByOrderSheetItemId?.[key] ?? 0) + (result.total[key]?.amount ?? 0),
            }
        })

        /** 객체인 sellerCustomerManagement와 logeyeRewardPointByTitle는 mergeWith로 title별로 합산  */
        result.total.sellerCustomerManagementByTitle = mergeWith(
            (prev = 0, next = 0) => prev + next,
            {...result.total.sellerCustomerManagementByTitle},
            pointByOrderSheetItemId.sellerCustomerManagementByTitle,
        )
        result.total.logeyeRewardPointByTitle = mergeWith(
            (
                prev: LogeyeRewardPoint = {rewardAmount: 0, membershipReward: false},
                next: LogeyeRewardPoint = {rewardAmount: 0, membershipReward: false},
            ) => {
                return {
                    rewardAmount: prev.rewardAmount + next.rewardAmount,
                    membershipReward: prev.membershipReward || next.membershipReward,
                }
            },
            {...result.total.logeyeRewardPointByTitle},
            pointByOrderSheetItemId.logeyeRewardPointByTitle,
        )
    })
    /** expectedNaverBankbook과 expectedChargePoint는 C2에서는 PurchaseRewardPoint 밖에 총합이 따로 제공되므로 따로 합산합니다. */
    result.total.expectedNaverBankbook += expectedNaverBankbook ?? 0
    result.total.expectedChargePoint += expectedChargePoint ?? 0

    return result
})

const calcMaxReviewRewardPoint = (reviewRewardPoint: ReviewRewardPoint) => {
    const {afterUsePhotoVideoReview, afterUseTextReview, writePhotoVideoReview, writeTextReview, subscriberReview} =
        reviewRewardPoint
    return (
        Math.max(afterUsePhotoVideoReview, afterUseTextReview) +
        Math.max(writePhotoVideoReview, writeTextReview) +
        (subscriberReview ?? 0)
    )
}

export const reviewRewardSelector = createSelector(rewardSelector, ({reviewRewardPoint}) => {
    const {pointsByOrderSheetItemId} = reviewRewardPoint
    const result = createInitialReviewRewardPoint()
    Object.entries(pointsByOrderSheetItemId).forEach(([itemId, pointByOrderSheetItemId]) => {
        result.pointsByOrderSheetItemId[itemId] = {
            ...pointByOrderSheetItemId,
            maxRewardPoint: calcMaxReviewRewardPoint(pointByOrderSheetItemId),
        }
        Object.keys(result.total).forEach((originKey) => {
            const key = originKey as keyof ReviewRewardPoint
            result.total[key] += result.pointsByOrderSheetItemId[itemId]?.[key] ?? 0
        })
    })

    return result
})

export const totalReviewRewardSelector = createSelector(reviewRewardSelector, ({total}) => {
    return total
})

export const accumulateMembershipPointSelector = (state: RootState) => state.reward.purchaseRewardPoint.total.membership

/** 혜택카드 적립금 */
export const cardEasyPayRewardPointSelector = (state: RootState) => state.reward.cardEasyPayRewardPoint

/** 예약추가적립 총합 */
export const totalEasyBookingRewardPointSelector = (state: RootState) => state.reward.easyBookingRewardPoint.total

const filterLogeyeRewardByMembership = (pointsByTitle: LogeyeRewardPointByTitle, membership: boolean) => {
    return Object.entries(pointsByTitle)
        .filter(([, {membershipReward}]) => membership === membershipReward)
        .map(([title, {rewardAmount}]) => {
            return {
                name: title,
                amount: rewardAmount,
            }
        })
}

/** 로그아이 적립 리워드를 맴버쉽 여부로 분류해서 리턴하는 selector */
export const logeyeRewardSelector = createSelector(purchaseRewardSelector, ({total: {logeyeRewardPointByTitle}}) => {
    return {
        inflowBenefitMileages: filterLogeyeRewardByMembership(logeyeRewardPointByTitle, false),
        inflowBenefitMembershipMileages: filterLogeyeRewardByMembership(logeyeRewardPointByTitle, true),
    }
})

/** 판매자관리 추가 적립 리워드 selector */
export const sellerCustomerManagementMileagesSelector = createSelector(
    purchaseRewardSelector,
    ({total: {sellerCustomerManagementByTitle}}) => {
        return Object.entries(sellerCustomerManagementByTitle).map(([name, amount]) => {
            return {name, amount}
        })
    },
)

/** 적립금 + 리뷰적립금 */
export const totalMileageSelector = createSelector(
    purchaseRewardSelector,
    totalReviewRewardSelector,
    cardEasyPayRewardPointSelector,
    totalEasyBookingRewardPointSelector,
    (
        {total: {totalRewardPoint: totalPurchaseRewardPoint}},
        {maxRewardPoint: maxReviewRewardPoint},
        totalCardEasyPayRewardPoint,
        totalEasyBookingRewardPoint,
    ) => {
        return (
            totalPurchaseRewardPoint + maxReviewRewardPoint + totalCardEasyPayRewardPoint + totalEasyBookingRewardPoint
        )
    },
)

/** 주문완료 > 로그아이 적립 리워드를 맴버쉽 여부로 분류해서 리턴하는 selector */
export const resultLogeyeRewardSelector = createSelector(
    rewardSelector,
    ({
        purchaseRewardPoint: {
            total: {logeyeRewardPointByTitle},
        },
    }) => {
        return {
            inflowBenefitMileages: filterLogeyeRewardByMembership(logeyeRewardPointByTitle, false),
            inflowBenefitMembershipMileages: filterLogeyeRewardByMembership(logeyeRewardPointByTitle, true),
        }
    },
)

/** 구매적립에 대한 정보를 반환하는 selector */
export const purchaseRewardInfoSelector = createSelector(rewardSelector, ({items}) => {
    /** 구매적립 목록 */
    const purchaseRewardItems = items.filter(({type}) => type === RewardType.PURCHASE)
    /** 구매적립 총 금액 */
    const totalAmount = purchaseRewardItems.reduce((acc, {amount}) => acc + amount, 0)
    /** 구매적립 목록을 번들 단위로 grouping */
    const itemsByBundleId = groupBy(
        'bundleId',
        items.filter(({type}) => type === RewardType.PURCHASE),
    )

    /** 번들별 구매적립 총 합산 금액 */
    const totalAmountByBundleId = mapValues((bundledItems) => sumBy('amount', bundledItems), itemsByBundleId)
    /** 모든 번들의 구매적립 동일 항목들을 합산해서 재배열 */
    const itemsMergedByTitle = purchaseRewardItems
        .filter((reward): reward is Omit<RewardItem, 'title'> & {title: string} => !!reward.title)
        .reduce<(Omit<RewardItem, 'title'> & {title: string})[]>((acc, reward) => {
            const prevIndex = acc.findIndex(({title}) => title === reward.title)
            const targetItem = acc[prevIndex]

            if (targetItem) {
                acc[prevIndex] = {...targetItem, amount: targetItem.amount + reward.amount}
            } else {
                acc.push(reward)
            }

            return acc
        }, [])

    const purchaseMembershipItems = items.filter(
        ({type, subType}) => type === RewardType.PURCHASE && subType === PurchaseSubType.MEMBERSHIP,
    )

    const totalPurchaseMembershipAmount = purchaseMembershipItems.reduce((acc, {amount}) => acc + amount, 0)

    return {
        totalAmount,
        itemsByBundleId,
        totalAmountByBundleId,
        itemsMergedByTitle,
        purchaseMembershipItems,
        totalPurchaseMembershipAmount,
    }
})

type ReviewCategoryType =
    | 'writeTextAmount'
    | 'writePhotoVideoAmount'
    | 'afterUseTextAmount'
    | 'subscriberAmount'
    | 'afterUsePhotoVideoAmount'
    | 'writeTotalAmount'
    | 'afterUseTotalAmount'
    | 'reviewTotalAmount'
    | 'writtenWriteTextAmount'
    | 'writtenWritePhotoVideoAmount'
    | 'writtenAfterUseTextAmount'
    | 'writtenSubscriberAmount'
    | 'writtenAfterUsePhotoVideoAmount'

const WrittenReviewSubType = [
    ReviewSubType.WRITTEN_WRITE_TEXT_REVIEW,
    ReviewSubType.WRITTEN_WRITE_PHOTO_VIDEO_REVIEW,
    ReviewSubType.WRITTEN_AFTER_USE_PHOTO_VIDEO_REVIEW,
    ReviewSubType.WRITTEN_AFTER_USE_TEXT_REVIEW,
    ReviewSubType.WRITTEN_SUBSCRIBER_REVIEW,
]

/** 리뷰적립에 대한 정보를 반환하는 selector */
export const reviewRewardInfoSelector = createSelector(rewardSelector, ({items}) => {
    /** 리뷰적립 목록 */
    const reviewRewardItems = items.filter(({type}) => type === RewardType.REVIEW)

    /** 리뷰적립 스냅샷 정보 목록 */
    const beforeReviewRewardItems = reviewRewardItems.filter(({subType}) => !includes(subType, WrittenReviewSubType))
    /** 리뷰적립 이력 정보 목록 */
    const writtenReviewRewardItems = reviewRewardItems.filter(({subType}) => includes(subType, WrittenReviewSubType))

    /** 리뷰적립 스냅샷 총 금액 */
    const beforeReviewTotalAmount = beforeReviewRewardItems.reduce((acc, next) => {
        if (next?.subType === ReviewSubType.REVIEW_TOTAL) {
            return acc + next.amount
        }

        return acc
    }, 0)

    /** 리뷰작성이 완료된 상품에 대한 리뷰적립 총 금액 */
    const writtenReviewTotalAmount = writtenReviewRewardItems.reduce((acc, next) => {
        return acc + next.amount
    }, 0)

    /** 리뷰적립 목록을 번들 단위로 grouping */
    const itemsByBundleId = groupBy('bundleId', reviewRewardItems)

    /** 번들ID별 > 리뷰적립 유형 정보 */
    const amountInfoByBundleId = mapValues((item) => {
        const initialInfo: {[key in ReviewCategoryType]: number} = {
            writeTextAmount: 0,
            writePhotoVideoAmount: 0,
            afterUseTextAmount: 0,
            subscriberAmount: 0,
            afterUsePhotoVideoAmount: 0,
            writeTotalAmount: 0,
            afterUseTotalAmount: 0,
            reviewTotalAmount: 0,
            writtenWriteTextAmount: 0,
            writtenWritePhotoVideoAmount: 0,
            writtenAfterUseTextAmount: 0,
            writtenSubscriberAmount: 0,
            writtenAfterUsePhotoVideoAmount: 0,
        }

        return item.reduce(
            (info, next) => {
                const reviewInfoMap: Record<ReviewSubTypeType, ReviewCategoryType> = {
                    [ReviewSubType.WRITE_TEXT_REVIEW]: 'writeTextAmount',
                    [ReviewSubType.WRITE_PHOTO_VIDEO_REVIEW]: 'writePhotoVideoAmount',
                    [ReviewSubType.AFTER_USE_TEXT_REVIEW]: 'afterUseTextAmount',
                    [ReviewSubType.SUBSCRIBER_REVIEW]: 'subscriberAmount',
                    [ReviewSubType.AFTER_USE_PHOTO_VIDEO_REVIEW]: 'afterUsePhotoVideoAmount',
                    [ReviewSubType.WRITE_TOTAL]: 'writeTotalAmount',
                    [ReviewSubType.AFTER_USE_WRITE_TOTAL]: 'afterUseTotalAmount',
                    [ReviewSubType.REVIEW_TOTAL]: 'reviewTotalAmount',
                    [ReviewSubType.WRITTEN_WRITE_TEXT_REVIEW]: 'writtenWriteTextAmount',
                    [ReviewSubType.WRITTEN_WRITE_PHOTO_VIDEO_REVIEW]: 'writtenWritePhotoVideoAmount',
                    [ReviewSubType.WRITTEN_AFTER_USE_TEXT_REVIEW]: 'writtenAfterUseTextAmount',
                    [ReviewSubType.WRITTEN_AFTER_USE_PHOTO_VIDEO_REVIEW]: 'writtenAfterUsePhotoVideoAmount',
                    [ReviewSubType.WRITTEN_SUBSCRIBER_REVIEW]: 'writtenSubscriberAmount',
                }

                const key = next.subType && isReviewSubType(next.subType) ? reviewInfoMap[next.subType] : undefined

                if (key) {
                    info[key] += next.amount
                }

                return info
            },
            {...initialInfo},
        )
    }, itemsByBundleId)

    return {
        beforeReviewTotalAmount,
        writtenReviewTotalAmount,
        totalAmount: beforeReviewTotalAmount + writtenReviewTotalAmount,
        amountInfoByBundleId,
    }
})

/** 혜택카드적립에 대한 정보를 반환하는 selector */
export const benefitCardRewardInfoSelector = createSelector(rewardSelector, ({items}) => {
    /** 혜택카드적립 목록 */
    const benefitCardRewardItems = items.filter(({type}) => type === RewardType.BENEFIT_CARD)
    /** 혜택카드적립 총 금액 */
    const totalAmount = benefitCardRewardItems.reduce((acc, {amount}) => acc + amount, 0)

    return {
        totalAmount,
    }
})

/** 멤버십 예상적립금 반환 selector */
export const expectedMembershipRewardInfoSelector = createSelector(rewardSelector, ({items}) => {
    /** 멤버십 예상적립 목록 */
    const expectedMembershipItems = items.filter(({type}) => type === RewardType.EXPECTED_MEMBERSHIP)
    /** 멤버십 예상적립금 총합 */
    const totalAmount = expectedMembershipItems.reduce((acc, {amount}) => acc + amount, 0)
    /** 멤버십적립 목록을 번들 단위로 grouping */
    const itemsByBundleId = groupBy(
        'bundleId',
        items.filter(({type}) => type === RewardType.EXPECTED_MEMBERSHIP),
    )

    /** 번들별 구매적립 총 합산 금액 */
    const totalAmountByBundleId = mapValues((bundledItems) => sumBy('amount', bundledItems), itemsByBundleId)

    /** 모든 번들의 멤버십 예상적립 동일 항목들을 합산해서 재배열 */
    const itemsMergedByTitle = expectedMembershipItems
        .filter((reward): reward is Omit<RewardItem, 'title'> & {title: string} => !!reward.title)
        .reduce<(Omit<RewardItem, 'title'> & {title: string})[]>((acc, reward) => {
            const prevIndex = acc.findIndex(({title}) => title === reward.title)
            const targetItem = acc[prevIndex]

            if (targetItem) {
                acc[prevIndex] = {...targetItem, amount: targetItem.amount + reward.amount}
            } else {
                acc.push(reward)
            }

            return acc
        }, [])

    return {
        itemsMergedByTitle,
        totalAmount,
        totalAmountByBundleId,
    }
})

/** 네이버예약 적립정보에 대한 정보를 반환하는 selector */
export const easyBookingRewardInfoSelector = createSelector(rewardSelector, ({items}) => {
    /** 네이버예약 적립 목록 */
    const easyBookingRewardItems = items.filter(({type}) => type === RewardType.EASY_BOOKING)
    /** 네이버예약 적립 총 금액 */
    const totalAmount = easyBookingRewardItems.reduce((acc, {amount}) => acc + amount, 0)

    return {
        totalAmount,
        easyBookingRewardItems,
    }
})

/** 이벤트 적립 정보 selector */
export const eventRewardInfoSelector = createSelector(rewardSelector, ({items}) => {
    const eventRewardItems = items.filter(({type}) => type === RewardType.EVENT)

    /** 이벤트 적립 목록 */
    const itemsWithTitle = eventRewardItems.filter(
        (reward): reward is Omit<RewardItem, 'title'> & {title: string} => !!reward.title,
    )
    /** 이벤트 적립 총 적립금 */
    const totalAmount = eventRewardItems.reduce((acc, {amount}) => acc + amount, 0)

    return {
        itemsWithTitle,
        totalAmount,
    }
})

/** 적립 아이템 총합 selector */
export const totalRewardItemsAmountSelector = createSelector(
    purchaseRewardInfoSelector,
    reviewRewardInfoSelector,
    benefitCardRewardInfoSelector,
    expectedMembershipRewardInfoSelector,
    easyBookingRewardInfoSelector,
    eventRewardInfoSelector,
    (
        {totalAmount: totalPurchaseRewardAmount},
        {totalAmount: totalReviewRewardAmount},
        {totalAmount: totalBenefitCardRewardAmount},
        {totalAmount: totalMembershipRewardAmount},
        {totalAmount: totalEasyBookingRewardAmount},
        {totalAmount: totalEventRewardAmount},
    ) => {
        return (
            // 구매 적립
            totalPurchaseRewardAmount +
            // 리뷰 적립
            totalReviewRewardAmount +
            // 혜택카드 적립
            totalBenefitCardRewardAmount +
            // 멤버십 적립
            totalMembershipRewardAmount +
            // 네이버예약 적립
            totalEasyBookingRewardAmount +
            // 이벤트 적립
            totalEventRewardAmount
        )
    },
)
